
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../Presentation/hooks';
import { User } from '../../../User/Model/User';
import { SlotSearchParams } from '../../Model/Slot';
import { fetchSlots, fetchSlotsPaginationInfo } from '../actions';
import { resetRefreshFlag, clearDefaultPage } from '../reducers';
import { 
  selectSlotError, 
  selectSlotLoading, 
  selectSlots,
  selectSlotPagination,
  selectSlotFilters,
  selectSlotNeedsRefresh,
  selectCurrentPage,
  selectPageSize,
  selectTotalCount,
  selectTotalPages,
  selectHasNext,
  selectHasPrevious,
  selectDefaultPageLoaded
} from '../selectors';

export const useSlots = (me: User, params?: SlotSearchParams) => {
  const dispatch = useAppDispatch();
  const slots    = useAppSelector(selectSlots);
  const loading  = useAppSelector(selectSlotLoading);
  const error    = useAppSelector(selectSlotError);
  const pagination = useAppSelector(selectSlotPagination);
  const filters = useAppSelector(selectSlotFilters);
  const needsRefresh = useAppSelector(selectSlotNeedsRefresh);
  const defaultPageLoaded = useAppSelector(selectDefaultPageLoaded);

  const refresh = () => dispatch(fetchSlots({ me, params }));

  // params가 변경될 때마다 자동으로 fetchSlots 호출
  React.useEffect(() => {
    if (me) {
      
      // 기본 페이지인지 확인
      // - 검색 조건 없음
      // - 페이지 1, 기본 페이지 크기 20
      // - 정렬이 기본값(registered desc)
      const isDefaultPage = !params || (
        (!params.slotId && !params.keyword && !params.url && !params.sellerId &&
         !params.creatorId && !params.mid_single && !params.mid_compare) && 
        params.page === 1 &&
        params.pageSize === 20 &&
        (params.order_by === undefined || params.order_by === 'registered') &&
        (params.order_dir === undefined || params.order_dir === 'desc')
      );
            
      // 기본 페이지가 아니거나 기본 페이지지만 캐시가 없을 때만 API 호출
      if (!isDefaultPage || !defaultPageLoaded) {
        dispatch(fetchSlots({ me, params }));
      } else {
      }
    }
  }, [dispatch, me, params, defaultPageLoaded]);

  // needsRefresh가 true일 때 자동으로 새로고침
  React.useEffect(() => {
    if (needsRefresh && me) {
      // 기본 페이지 캐시 무효화
      dispatch(clearDefaultPage());
      // 즉시 새로고침 실행
      dispatch(fetchSlots({ me, params }));
      dispatch(resetRefreshFlag()); // 새로고침 플래그 리셋
    }
  }, [needsRefresh, dispatch, me, params]);

  return { 
    slots, 
    loading, 
    error, 
    pagination,
    filters,
    needsRefresh,
    refresh 
  };
};

export const useSlotPagination = (me: User, params?: SlotSearchParams) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector(selectCurrentPage);
  const pageSize = useAppSelector(selectPageSize);
  const totalCount = useAppSelector(selectTotalCount);
  const totalPages = useAppSelector(selectTotalPages);
  const hasNext = useAppSelector(selectHasNext);
  const hasPrevious = useAppSelector(selectHasPrevious);
  const loading = useAppSelector(selectSlotLoading);
  const error = useAppSelector(selectSlotError);

  const refreshPaginationInfo = () => dispatch(fetchSlotsPaginationInfo({ me, params }));

  return {
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasNext,
    hasPrevious,
    loading,
    error,
    refreshPaginationInfo
  };
};
